import React, { useState } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiGallection, PapiPremiumFolderData } from '@wix/da-papi-types';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import DeviationDumbView from '@wix/da-shared-react/pkg/DeviationViews/Dumbo';
import ExcerptRenderer from '@wix/da-shared-react/pkg/TextRenderer/ExcerptRenderer';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import PremiumGalleryIcon from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import DiscountIcon from '@wix/da-ds/pkg/Icons/16x16/Discount';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import GallectionTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Gallection';
import PremiumFolderPrice from './PremiumFolderPrice';
import DiscountsPopup from './DiscountsPopup';
import FolderInfo from './FolderInfo';

import s from './PremiumGalleryPaywallLayout.scss';

export interface Props {
  className?: string;
  icon?: React.ReactNode;
  folder: PapiGallection;
  button: React.ReactNode;
  nonClickable?: boolean;
  premiumFolderData: PapiPremiumFolderData;
}

const PremiumGalleryPaywallLayout: React.FC<Props> = ({
  icon = null,
  folder,
  button,
  nonClickable = false,
}) => {
  const { t } = useTranslation();
  const [showDicountsPopup, setShowDiscountsPopup] = useState(false);
  const {
    numSubscribers,
    isPaidFolder,
    isWatchToAccess,
    eligibleDiscount,
    notEligibleDiscounts,
  } = normalizePremiumFolderData(folder.premiumFolderData);

  const itemCount = folder.totalItemCount ?? folder.size;
  const thumb = folder.thumb ?? folder?.additionalThumbs?.[0];

  return (
    <OverlayContextProvider
      value={{
        showPremiumFolderOverlay: false,
        showPremiumFolderIcon: false,
        showThumbStatusIndicators: false,
      }}
    >
      <div className={s['root']}>
        <div className={s['content']}>
          <div className={s['cover-wrap']}>
            {thumb && (
              <>
                {icon && <div className={s['cover-icon']}>{icon}</div>}
                <DeviationDumbView
                  className={s['cover-image']}
                  deviation={thumb}
                  width={375}
                  height={250}
                  useCoverMode={true}
                />
              </>
            )}
          </div>
          <div className={s['info-wrap']}>
            <div className={s['info-header']}>
              {(isPaidFolder || isWatchToAccess) && (
                <div className={s['info-header--top']}>
                  <PremiumGalleryIcon className={s['folder-icon']} />
                  <div className={s['folder-type']}>
                    {t(
                      isPaidFolder
                        ? 'paywall.premium.folder.title'
                        : 'paywall.watchersOnly.folder.title'
                    )}
                  </div>
                </div>
              )}

              <div className={s['info-header--middle']}>
                <div className={s['title']}>
                  <div className={s['folder-name']}>{folder.name}</div>
                </div>
                <PremiumFolderPrice
                  className={s['discount-price']}
                  folder={folder}
                />
              </div>

              <div className={s['info-header--bottom']}>
                <FolderInfo
                  className={s['sub']}
                  deviationCount={itemCount}
                  numSubscribers={numSubscribers}
                />
                {notEligibleDiscounts && eligibleDiscount && (
                  <div className={s['discounts']}>
                    <TextButton
                      key="save"
                      size="medium"
                      variant="main"
                      priority="secondary"
                      className={s['btn-show-discounts']}
                      onClick={() => setShowDiscountsPopup(true)}
                      icon={DiscountIcon}
                      iconClassName={s['icon-discount']}
                    >
                      {t('paywall.premium.discounts.button.viewAll')}
                    </TextButton>
                  </div>
                )}
                {notEligibleDiscounts && !eligibleDiscount && (
                  <div className={s['discounts']}>
                    <TextButton
                      key="save"
                      size="medium"
                      variant="main"
                      priority="secondary"
                      className={s['btn-show-discounts']}
                      onClick={() => setShowDiscountsPopup(true)}
                    >
                      {t('paywall.premium.discounts.button.availableDiscounts')}
                    </TextButton>
                  </div>
                )}
              </div>
            </div>
            <div className={s['info-body']}>
              {folder?.richDescription && (
                <div className={s['text']}>
                  {folder?.richDescription?.html.type === 'writer' && (
                    <ExcerptRenderer
                      className={s['description']}
                      htmlExcerptLegacy
                      legacyTheme={'comments'}
                      textContent={folder.richDescription}
                    />
                  )}
                  {folder?.richDescription?.html.type === 'tiptap' && (
                    <TextRenderer
                      className={s['description']}
                      tipTapPresetId="gallection"
                      tipTapViewer={GallectionTiptapViewer}
                      cookieType={MeasuredCookieType.GALLECTION_FOLDER_PAYWALL}
                      defaultSize={{
                        desktop: { width: 470, height: 800 },
                        mobile: { width: 380, height: 400 },
                      }}
                      textContent={folder.richDescription}
                    />
                  )}
                </div>
              )}
            </div>
            <div className={s['info-footer']}>
              <span
                className={c(
                  s['button'],
                  nonClickable && s['non-clickable-button']
                )}
              >
                {button}
              </span>
            </div>
          </div>
        </div>
      </div>
      <DiscountsPopup
        isOpen={showDicountsPopup}
        onRequestClose={() => setShowDiscountsPopup(false)}
        discounts={notEligibleDiscounts}
      />
    </OverlayContextProvider>
  );
};
PremiumGalleryPaywallLayout.displayName = 'PremiumGalleryPaywallLayout';

export default PremiumGalleryPaywallLayout;
